import { useLocalization } from "@fluent/react";
import { TextInput } from "@narmi/design_system";
import {
  PhoneTextInput,
  ContextForm,
  useFormData,
  validateEmail,
} from "cerulean";
import { useOrganizationUserContext } from "byzantine/src/OrganizationUser/OrganizationUserProvider";
import NewUserActions from "./NewUserActions";
import styles from "./NewUserFlow.module.scss";

export interface NewUserFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

interface NewUserInfoProps {
  initialData?: Partial<NewUserFormData>;
  onNext: (data: NewUserFormData) => void;
  onCancel: () => void;
  isPending?: boolean;
}

export function NewUserInfo({
  initialData = {},
  onNext,
  onCancel,
  isPending = false,
}: NewUserInfoProps) {
  const { l10n } = useLocalization();
  const { organizationUsers } = useOrganizationUserContext();
  const { formData, onChange } = useFormData<NewUserFormData>({
    first_name: initialData.first_name || "",
    last_name: initialData.last_name || "",
    email: initialData.email || "",
    phone: initialData.phone || "",
  });

  // Don't render the form until l10n bundles are loaded
  if (l10n.areBundlesEmpty?.()) {
    return null;
  }

  const validateEmailField = (value: string) => {
    const emailFormatError = validateEmail(
      value,
      false,
      l10n.getString("new-user-validation-email-invalid")
    );
    if (emailFormatError) {
      return emailFormatError;
    }

    const emailLower = value?.toLowerCase() || "";
    const emailExists = organizationUsers.some(
      (user) => user.user.email?.toLowerCase() === emailLower
    );

    if (emailExists) {
      return l10n.getString("new-user-validation-email-in-use");
    }

    return null;
  };

  const validatePhoneField = (value: string) => {
    if (!value?.trim()) {
      return l10n.getString("new-user-validation-phone-required");
    }

    const digitsOnly = (value || "").replace(/[^0-9]/g, "");
    if (digitsOnly.length < 10) {
      return l10n.getString("new-user-validation-phone-min-digits");
    }

    return null;
  };

  const handleNext = (callback: () => void) => {
    onNext(formData as NewUserFormData);
    callback();
  };

  return (
    <div style={{ minHeight: "800px" }}>
      <div className={styles.flowContainer}>
        <div className="section-box">
          <h2 className={styles.stepTitle}>
            {l10n.getString("new-user-info-title")}
          </h2>
          <p className={styles.stepSubtitle}>
            {l10n.getString("new-user-info-subtitle")}
          </p>

          <ContextForm data={formData} onChange={onChange} nativeForm={false}>
            <div className={styles.formContent}>
              <div>{l10n.getString("label-first-name")}</div>
              <ContextForm.Field required>
                <TextInput field="first_name" />
              </ContextForm.Field>

              <div>{l10n.getString("label-last-name")}</div>
              <ContextForm.Field required>
                <TextInput field="last_name" />
              </ContextForm.Field>

              <div>{l10n.getString("label-email")}</div>
              <ContextForm.Field required validate={validateEmailField}>
                <TextInput field="email" startIcon="mail" />
              </ContextForm.Field>

              <div>{l10n.getString("label-phone-number")}</div>
              <ContextForm.Field required validate={validatePhoneField}>
                <PhoneTextInput field="phone" startIcon="phone" />
              </ContextForm.Field>
            </div>

            <NewUserActions
              onSubmit={handleNext}
              onCancel={onCancel}
              isDisabled={isPending}
              submitLabel="Next"
              showBackButton={false}
            />
          </ContextForm>
        </div>
      </div>
    </div>
  );
}

export default NewUserInfo;
